import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Trophy } from 'lucide-react';
import AdminPageHeader from '@/components/layout/PageHeader';
import { api } from '@/lib/api';
import { unwrapList } from '@/lib/api-helpers';
import { PendentesContent } from '@/components/sorteios/PendentesContent';
import { EncerradosContent } from '@/components/sorteios/EncerradosContent';

export const Route = createFileRoute('/_admin/sorteios')({
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  component: SorteiosPage,
});

interface LotepStructure {
  id: string;
  status: string;
  pools?: { status: string }[];
  final_draw_at?: string;
  final_draws?: { id: string }[];
  final_draw_prizes?: { quantity: number }[];
  has_final_draw?: boolean;
}

// Pendente = estrutura ativa com sorteio final configurado que ainda não foi
// realizado. Não exige pool exausta nem data vencida — o admin decide quando
// executar. (Antes exigia pool exausta OU data no passado, o que escondia
// estruturas com pool aberta e sorteio futuro, ex.: "Teste 02".)
function isPending(s: LotepStructure): boolean {
  const hasFinalDrawConfigured = (s.final_draw_prizes ?? []).length > 0;
  const alreadyDrawn = s.has_final_draw === true || (s.final_draws ?? []).length > 0;
  return hasFinalDrawConfigured && !alreadyDrawn;
}

function SorteiosPage() {
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const initialTab = tab && ['pendentes', 'historico'].includes(tab) ? tab : 'pendentes';
  const [activeTab, setActiveTab] = useState(initialTab);

  // Badge count pra tab Pendentes — usa a mesma queryKey do PendentesContent (cache compartilhado).
  const { data: structures } = useQuery<LotepStructure[]>({
    queryKey: ['admin-lotep-structures-active'],
    queryFn: async () => {
      const r = await api.get('/admin/lotep/structures?status=active');
      return unwrapList<LotepStructure>(r.data, 'structures');
    },
  });
  const pendingCount = (structures ?? []).filter(isPending).length;

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    navigate({ search: { tab: value }, replace: true });
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Trophy}
        title="Sorteios"
        description="Gerencie sorteios pendentes e histórico"
      />

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="pendentes" className="gap-2">
            Pendentes
            {pendingCount > 0 && (
              <Badge variant="destructive" className="h-5 min-w-5 px-1.5">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="pendentes" className="mt-6">
          <PendentesContent />
        </TabsContent>

        <TabsContent value="historico" className="mt-6">
          <EncerradosContent />
        </TabsContent>
      </Tabs>
    </div>
  );
}
